<template>
  <div v-if="visible" class="update-overlay" role="dialog" aria-modal="true">
    <div class="update-modal">
      <div class="update-modal__icon">
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="24" cy="24" r="24" fill="#c9956c" opacity="0.15" />
          <circle cx="24" cy="24" r="17" fill="#c9956c" opacity="0.2" />
          <path
            d="M24 14v10l-2.5-2.5"
            stroke="#c9956c"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M24 14l2.5 2.5"
            stroke="#c9956c"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <rect
            x="12"
            y="28"
            width="24"
            height="7"
            rx="3"
            fill="#c9956c"
            opacity="0.25"
          />
          <path
            d="M16 31.5h16"
            stroke="#c9956c"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <div class="update-modal__content">
        <h2>Atualização disponível</h2>
        <p class="update-modal__versions">
          <span class="version-tag version-tag--old"
            >v{{ currentVersion }}</span
          >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            width="14"
            height="14"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
          <span class="version-tag version-tag--new">v{{ newVersion }}</span>
        </p>
        <p v-if="releaseNotes" class="update-modal__notes">
          {{ releaseNotes }}
        </p>
        <p class="update-modal__hint">
          Baixe e instale a nova versão para continuar usando o app com todas as
          melhorias.
        </p>
      </div>

      <p class="update-modal__download-hint">
        O arquivo APK será baixado. Abra-o na barra de notificações para
        instalar.
      </p>

      <div class="update-modal__actions">
        <button class="update-btn update-btn--primary" @click="baixarApk">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 3v13M5 13l7 7 7-7" />
            <path d="M3 20h18" />
          </svg>
          Baixar atualização
        </button>
        <button class="update-btn update-btn--secondary" @click="remindLater">
          Lembrar depois
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  APP_VERSION,
  APP_VERSION_CODE,
  VERSION_CHECK_URL,
} from "@/config/appVersion";
import { isNativeApp } from "@/utils/platform";

const SNOOZE_KEY = "update-snooze-until";
const SNOOZE_HOURS = 24;

function compareVersions(a, b) {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

export default {
  name: "UpdatePrompt",
  data() {
    return {
      visible: false,
      currentVersion: APP_VERSION,
      newVersion: "",
      apkUrl: "",
      releaseNotes: "",
    };
  },
  async mounted() {
    if (!isNativeApp()) return;

    try {
      const res = await fetch(`${VERSION_CHECK_URL}?t=${Date.now()}`);
      if (!res.ok) return;
      const data = await res.json();

      const serverCode = Number(data.versionCode || 0);
      const hasUpdate =
        serverCode > APP_VERSION_CODE ||
        compareVersions(data.version, APP_VERSION) > 0;

      if (hasUpdate) {
        const apkUrl = data.downloadUrl || data.apkUrl;
        this.newVersion = data.version;
        this.apkUrl = apkUrl;
        this.releaseNotes = data.releaseNotes || "";

        this.$store.commit("SET_UPDATE_AVAILABLE", { apkUrl });

        const snoozeUntil = localStorage.getItem(SNOOZE_KEY);
        if (snoozeUntil && Date.now() < Number(snoozeUntil)) return;

        this.visible = true;
      }
    } catch {
      // silencioso — sem internet ou servidor fora do ar
    }
  },
  methods: {
    baixarApk() {
      // _system abre no navegador do sistema (Chrome), que dispara o download
      // e mostra notificação na barra para o usuário instalar
      window.open(this.apkUrl, "_system");
      // Snooze de 2 horas para o usuário ter tempo de instalar sem ver o modal de novo
      const until = Date.now() + 2 * 60 * 60 * 1000;
      localStorage.setItem(SNOOZE_KEY, String(until));
      this.visible = false;
    },
    remindLater() {
      const until = Date.now() + SNOOZE_HOURS * 60 * 60 * 1000;
      localStorage.setItem(SNOOZE_KEY, String(until));
      this.visible = false;
    },
  },
};
</script>

<style scoped>
.update-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  backdrop-filter: blur(3px);
}

.update-modal {
  background: var(--c-surface, #fff);
  border-radius: 20px;
  padding: 2rem 1.75rem 1.75rem;
  max-width: 360px;
  width: 100%;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  text-align: center;
}

.update-modal__icon svg {
  width: 64px;
  height: 64px;
}

.update-modal__content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.update-modal__content h2 {
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--c-text, #2d2520);
  letter-spacing: -0.02em;
}

.update-modal__versions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 0.25rem 0;
  color: var(--c-text-muted, #8a7a70);
}

.version-tag {
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  font-family: monospace;
}

.version-tag--old {
  background: #f3f0ed;
  color: #8a7a70;
  text-decoration: line-through;
}

.version-tag--new {
  background: #edf7ef;
  color: #3a7d44;
}

.update-modal__notes {
  font-size: 0.875rem;
  color: var(--c-text-muted, #8a7a70);
  line-height: 1.5;
}

.update-modal__hint {
  font-size: 0.8125rem;
  color: var(--c-text-muted, #8a7a70);
  line-height: 1.5;
}

.update-modal__download-hint {
  font-size: 0.75rem;
  color: var(--c-text-muted, #8a7a70);
  line-height: 1.5;
  background: rgba(201, 149, 108, 0.08);
  border: 1px solid rgba(201, 149, 108, 0.2);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  text-align: center;
  width: 100%;
}

.update-modal__actions {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  width: 100%;
}

.update-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.8125rem 1.25rem;
  border-radius: 10px;
  font-family: var(--font, sans-serif);
  font-size: 0.9375rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s;
  border: none;
}

.update-btn svg {
  width: 17px;
  height: 17px;
  flex-shrink: 0;
}

.update-btn--primary {
  background: var(--c-accent, #c9956c);
  color: #fff;
}

.update-btn--primary:hover {
  background: #b07a52;
  transform: translateY(-1px);
}

.update-btn--secondary {
  background: var(--c-bg, #faf9f7);
  color: var(--c-text-muted, #8a7a70);
  border: 1px solid var(--c-border-light, #e8e0d8);
}

.update-btn--secondary:hover {
  color: var(--c-text, #2d2520);
}
</style>
